import { app } from 'electron';
import type { AiReportInput } from '../shared/types';
import { getSupabase } from './supabase/client';
import { requireUserId } from './supabase/auth';
import { t, uiLanguage } from './language';
import { normalizeFeatureRequest } from './featureRequestRules';

/**
 * Yapay zekâ içeriğini bildirme: Sohbet yanıtı, toplantı özeti ya da kayıt
 * sırasındaki soru-cevap "Bildir"le Supabase'deki `ai_content_reports`
 * tablosuna yazılır; ekip yönetim konsolunda (/admin/ai-reports) okur.
 * Google Play'in yapay zekâ içeriği politikası bildirimin uygulamadan
 * çıkmadan yapılabilmesini istiyor.
 *
 * Toplantı içerikleri şifreli saklandığı için yanıtın metni yalnızca kullanıcı
 * bildirim penceresinde "metni ekle"yi açınca gelir. Arayüzden gelen her alan
 * burada yeniden denetleniyor; veritabanı da aynı sınırları ve sıklık sınırını
 * ayrıca uyguluyor (0054_ai_content_reports.sql).
 */

const SURFACES = new Set(['chat', 'summary', 'live_ask']);
const REASONS = new Set(['offensive', 'harmful', 'inaccurate', 'other']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Karakter sınırları veritabanındakiyle aynı. */
const LIMITS = { note: 1000, content: 8000, prompt: 2000 } as const;

/** Görünmez/kontrol karakterlerini atar, sınıra göre (kod noktası) kırpar; boşsa null. */
function cleanText(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const text = [...normalizeFeatureRequest(value.slice(0, max * 4))].slice(0, max).join('').trim();
  return text || null;
}

/** Mobil paket web'in süreç değerlerini taşıyor (`web-browser`); ayrımı istemci adı yapıyor. */
function platformTag(): string {
  if (process.env.TALKNOTE_CLIENT_NAME === 'talknote-mobile') return 'mobile';
  return `${process.platform}-${process.arch}`.toLowerCase().slice(0, 32);
}

export async function submitAiReport(input: unknown): Promise<void> {
  const value = (input ?? {}) as Partial<AiReportInput>;
  if (!SURFACES.has(String(value.surface)) || !REASONS.has(String(value.reason))) {
    throw new Error(t('Bir gerekçe seçmelisin.', 'Please pick a reason.'));
  }

  await requireUserId();
  const content = cleanText(value.content, LIMITS.content);
  const { error } = await getSupabase()
    .from('ai_content_reports')
    .insert({
      surface: value.surface,
      reason: value.reason,
      note: cleanText(value.note, LIMITS.note),
      content,
      // Soru yalnızca yanıtla birlikte anlamlı; metin eklenmediyse o da gitmez.
      prompt: content ? cleanText(value.prompt, LIMITS.prompt) : null,
      meeting_id: typeof value.meetingId === 'string' && UUID.test(value.meetingId) ? value.meetingId : null,
      app_version: app.getVersion(),
      platform: platformTag(),
      ui_language: uiLanguage(),
    });

  if (error) {
    if (error.message.includes('ai_report_rate_limited')) {
      throw new Error(
        t('Kısa sürede çok sayıda bildirim gönderdin. Biraz sonra tekrar dene.', 'You’ve sent a lot of reports recently. Please try again later.')
      );
    }
    console.error('[aiReports] kaydedilemedi:', error.code, error.message);
    throw new Error(t('Bildirim gönderilemedi. Biraz sonra tekrar dene.', 'Could not send the report. Please try again later.'));
  }
}
